import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/Header';
import AlertBanner from './components/AlertBanner';
import LatestCalamityCard from './components/LatestCalamityCard';
import CalamityMap from './components/CalamityMap';
import CalamityFilters from './components/CalamityFilters';
import CalamityTable from './components/CalamityTable';
import StatusHealth from './components/StatusHealth';
import FaultLegend from './components/FaultLegend';
import VolcanoLegend from './components/VolcanoLegend';
import EmergencyNumbers from './components/EmergencyNumbers';
import Footer from './components/Footer';
import AboutSection from './components/AboutSection';
import EmergencyPreparedness from './components/EmergencyPreparedness';
import FAQSection from './components/FAQSection';
import IliganScorecard from './components/IliganScorecard';
import EmailAlerts from './components/EmailAlerts';

// Services
import calamityService from './services/calamityService';
import alertScheduler from './services/alertScheduler';

// SEO and Performance optimizations
const Dashboard = () => {
  const [calamities, setCalamities] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [healthStatus, setHealthStatus] = useState(null);
  const [userLocation, setUserLocation] = useState(null); // For email alerts

  // Filter states
  const [timeWindow, setTimeWindow] = useState('72h');
  const [minMagnitude, setMinMagnitude] = useState(4.5);
  const [showOffshore, setShowOffshore] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState(['earthquake', 'volcanic', 'flood', 'weather', 'tsunami', 'tidal']);
  const [selectedCalamity, setSelectedCalamity] = useState(null);

  // State for showing events in modal/table
  const [showEvents, setShowEvents] = useState({ isOpen: false, events: [], title: '' });

  // Get user location for email alerts
  useEffect(() => {
    // In a real app, you might use the browser's geolocation API
    // For demo purposes, we'll use a fixed location
    setUserLocation('Philippines');
  }, []);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchCalamityData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await calamityService.getAllCalamities(timeWindow, minMagnitude);
      setCalamities(data.events);
      setLastUpdate(data.lastUpdate);
      setHealthStatus(calamityService.getHealthStatus());

      // Update page title with latest stats
      const activeCount = data.events.filter(c => c.alert !== 'None').length;
      document.title = activeCount > 0
        ? `(${activeCount}) Philippines Disaster Risk Portal - Real-time Monitoring`
        : 'Philippines Disaster Risk Portal - Real-time Multi-Hazard Monitoring';
    } catch (error) {
      console.error('Error fetching calamity data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [timeWindow, minMagnitude]);

  // Memoized filtered calamities
  const filteredCalamities = useMemo(() => {
    return calamities.filter((c) => {
      if (!selectedTypes.includes(c.type)) return false;
      if (c.type === 'earthquake' && c.magnitude < minMagnitude) return false;
      if (!showOffshore && !c.nearest_city) return false;
      return true;
    });
  }, [calamities, selectedTypes, minMagnitude, showOffshore]);

  // Memoized calamity stats
  const calamityStats = useMemo(() => ({
    warningCount: filteredCalamities.filter((c) => c.alert === 'Warning').length,
    watchCount: filteredCalamities.filter((c) => c.alert === 'Watch').length,
  }), [filteredCalamities]);

  // Initial data fetch
  useEffect(() => {
    fetchCalamityData();
  }, [fetchCalamityData]);

  // Auto-refresh setup
  useEffect(() => {
    calamityService.startAutoRefresh(fetchCalamityData, 60000);
    return () => calamityService.stopAutoRefresh();
  }, [fetchCalamityData]);

  // Start alert scheduler
  useEffect(() => {
    alertScheduler.start();
    return () => alertScheduler.stop();
  }, []);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle calamity selection
  const handleCalamityClick = useCallback((calamity) => {
    setSelectedCalamity(calamity);
    document.getElementById('calamity-map')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handle alert click from scorecard
  const handleAlertClick = useCallback((alertEvents) => {
    if (alertEvents && alertEvents.length > 0) {
      // Show alerts in a modal or highlight them in the table
      setShowEvents({
        isOpen: true,
        events: alertEvents,
        title: 'Alert Events'
      });
      document.getElementById('calamity-table')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Handle "View All" click from scorecard
  const handleShowEvents = useCallback((events, title) => {
    if (events && events.length > 0) {
      setShowEvents({
        isOpen: true,
        events: events,
        title: title
      });
      document.getElementById('calamity-table')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    fetchCalamityData();
  }, [fetchCalamityData]);

  // Structured data for the dashboard
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      "name": "Philippines Disaster Risk Data",
      "description": "Real-time data about earthquakes, volcanic eruptions, floods, and other hazards across the Philippines",
      "url": window.location.href,
      "dateModified": lastUpdate?.toISOString() || new Date().toISOString(),
      "provider": {
        "@type": "Organization",
        "name": "Philippines Disaster Risk Portal"
      },
      "spatial": {
        "@type": "Country",
        "name": "Philippines"
      },
      "temporalCoverage": "2024/2025",
      "keywords": "earthquake, volcanic eruption, flood, tsunami, weather, Philippines"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [lastUpdate, filteredCalamities.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lastUpdate={lastUpdate} isOnline={isOnline} isLoading={isLoading} />
      <AlertBanner alerts={calamityStats} />
      
      <main id="main-content" className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <section aria-label="Filters and Controls">
          <CalamityFilters
            timeWindow={timeWindow}
            setTimeWindow={setTimeWindow}
            minMagnitude={minMagnitude}
            setMinMagnitude={setMinMagnitude}
            showOffshore={showOffshore}
            setShowOffshore={setShowOffshore}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            onRefresh={handleRefresh}
            isLoading={isLoading}
            calamityStats={calamityStats}
          />
        </section>

        <section aria-label="Philippines Summary">
          <IliganScorecard 
            calamities={filteredCalamities} 
            onAlertClick={handleAlertClick} 
            onShowEvents={handleShowEvents} 
          />
        </section>

        <section aria-label="Latest Calamities">
          <LatestCalamityCard calamities={filteredCalamities} />
        </section>

        {/* Email Alerts Section */}
        <section aria-label="Email Alerts">
          <EmailAlerts userLocation={userLocation} />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3" id="calamity-map">
            <section aria-label="Interactive Hazard Map">
              <CalamityMap
                calamities={filteredCalamities}
                onCalamityClick={handleCalamityClick}
                filters={{ types: selectedTypes }}
              />
            </section>
          </div>
          
          <aside className="lg:col-span-1">
            <section aria-label="System Status">
              <StatusHealth healthStatus={healthStatus} />
            </section>
          </aside>
        </div>

        {/* Volcanic Activity Section */}
        <section aria-label="Volcanic Activity Information">
          <div className="block lg:hidden">
            <VolcanoLegend />
          </div>
          <div className="hidden lg:block">
            <VolcanoLegend />
          </div>
        </section>

        <section aria-label="Fault Line Information">
          <FaultLegend />
        </section>

        <section aria-label="Detailed Calamity Data" id="calamity-table">
          <CalamityTable
            calamities={showEvents.isOpen ? showEvents.events : filteredCalamities}
            onCalamityClick={handleCalamityClick}
          />
          
          {showEvents.isOpen && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowEvents({ isOpen: false, events: [], title: '' })}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Show All Events
              </button>
            </div>
          )}
        </section>

        {/* Information Sections */}
        <AboutSection />
        <EmergencyPreparedness />
        <FAQSection />
        
        <section id="emergency" aria-label="Emergency Information">
          <EmergencyNumbers />
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

const StatusPage = () => {
  const [healthStatus, setHealthStatus] = useState(null);
  
  useEffect(() => {
    const fetchStatus = () => {
      const status = calamityService.getHealthStatus();
      setHealthStatus({
        ...status,
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        alertScheduler: alertScheduler.getStatus()
      });
    };
    
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = 'System Status - Philippines Disaster Risk Portal';
  }, []);

  if (!healthStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold mb-2">System Status</h1>
          <p className="text-gray-600">Real-time status of monitoring systems and data sources</p>
        </header>
        
        <main>
          <div className="bg-white rounded-lg shadow p-6">
            <pre className="text-sm bg-gray-100 p-4 rounded overflow-auto">
              {JSON.stringify(healthStatus, null, 2)}
            </pre>
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Set initial page title
    document.title = 'Philippines Disaster Risk Portal - Real-time Multi-Hazard Monitoring';
    
    // Add meta description dynamically
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Track earthquakes, volcanic eruptions, floods, tsunamis, and weather hazards across the Philippines in real-time. Official PAGASA, PHIVOLCS, and USGS data integration. Emergency preparedness guides and educational resources.';
    document.head.appendChild(metaDescription);
    
    // Add meta keywords
    const metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'Philippines disaster risk, earthquake monitoring, volcanic activity, flood warning, tsunami alert, weather tracking, PAGASA, PHIVOLCS, USGS, disaster preparedness, emergency management, Philippines natural disasters, hazard monitoring, safety guidelines';
    document.head.appendChild(metaKeywords);
    
    return () => {
      document.head.removeChild(metaDescription);
      document.head.removeChild(metaKeywords);
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/status" element={<StatusPage />} />
      </Routes>
    </Router>
  );
}

export default App;